import type { CSSMotionProps } from '@rc-component/motion';
import { useControlledState } from '@rc-component/util';
import React from 'react';
import initCollapseMotion from '../../_util/motion';

export type CollapsibleOptions = {
  /**
   * @desc 初始化展开的节点
   * @descEN default expanded keys
   */
  defaultExpandedKeys?: string[];

  /**
   * @desc 当前展开的节点
   * @descEN current expanded keys
   */
  expandedKeys?: string[];

  /**
   * @desc 展开节点变化回调
   * @descEN callback when expanded keys change
   */
  onExpand?: (expandedKeys: string[]) => void;
};

export type Collapsible = boolean | CollapsibleOptions;

type RequiredCollapsibleOptions = Required<CollapsibleOptions>;

type UseCollapsible = (
  collapsible?: Collapsible,
  prefixCls?: string,
  rootPrefixCls?: string,
) => [
  boolean,
  RequiredCollapsibleOptions['expandedKeys'],
  ((curKey: string) => void) | undefined,
  CSSMotionProps,
];

const useCollapsible: UseCollapsible = (collapsible, prefixCls, rootPrefixCls) => {
  const isThoughtChainUnControlled =
    typeof collapsible === 'boolean' || collapsible?.expandedKeys === undefined;
  // ============================ Collapsible ============================
  const [enableCollapse, defaultExpandedKeys, customizeExpandedKeys, customizeOnExpand] =
    React.useMemo(() => {
      let baseConfig: RequiredCollapsibleOptions = {
        expandedKeys: [],
        defaultExpandedKeys: [],
        onExpand: () => {},
      };

      if (!collapsible) {
        return [
          false,
          baseConfig.defaultExpandedKeys,
          baseConfig.expandedKeys,
          baseConfig.onExpand,
        ];
      }

      if (typeof collapsible === 'object') {
        baseConfig = { ...baseConfig, ...collapsible };
      }

      return [true, baseConfig.defaultExpandedKeys, baseConfig.expandedKeys, baseConfig.onExpand];
    }, [collapsible]);

  // ============================ ExpandedKeys ============================
  const [mergedExpandedKeys, setMergedExpandedKeys] = useControlledState<
    RequiredCollapsibleOptions['expandedKeys']
  >(defaultExpandedKeys || [], isThoughtChainUnControlled ? undefined : customizeExpandedKeys);

  // ============================ Event ============================
  const onItemExpand = (curKey: string) => {
    setMergedExpandedKeys((preKeys) => {
      const targetPreKeys = isThoughtChainUnControlled ? preKeys : customizeExpandedKeys;
      const keys = targetPreKeys.includes(curKey)
        ? targetPreKeys.filter((key) => key !== curKey)
        : [...targetPreKeys, curKey];
      customizeOnExpand?.(keys);
      return keys;
    });
  };

  // ============================ Motion ============================

  const collapseMotion: CSSMotionProps = React.useMemo(() => {
    if (!enableCollapse) return {};

    return {
      ...initCollapseMotion(rootPrefixCls),
      motionAppear: false,
      leavedClassName: `${prefixCls}-content-hidden`,
    };
  }, [rootPrefixCls, prefixCls, enableCollapse]);

  // ============================ Return ============================
  return [
    enableCollapse,
    mergedExpandedKeys,
    enableCollapse ? onItemExpand : undefined,
    collapseMotion,
  ];
};

export default useCollapsible;
